import { MAX_FILE_SIZE_BYTES, MEGA_BYTE_TO_BYTE } from '@brighte/validation';
import { convertHeicToJpeg, isHeicFile } from './heicConversion';

// iOS can hand the picker a JPEG larger than the original HEIC. Allow room to resize it
// locally, while keeping the existing 20 MB limit for documents sent to the backend.
export const MAX_DOCUMENT_SELECTION_SIZE_BYTES = 50 * MEGA_BYTE_TO_BYTE;
const JPEG_MIME_TYPE = 'image/jpeg';
const JPEG_QUALITY = 0.9;
// Match the backend's document image dimensions and preserve the aspect ratio.
const MAX_IMAGE_DIMENSION_PX = 2000;
// Spark Dropzone converts maxFileSizeKb to bytes by multiplying by 1000.
// Divide the byte limit by the same factor to preserve the exact selection boundary.
const KILOBYTE_TO_BYTE = 1000;
export const MAX_DOCUMENT_SELECTION_SIZE_KB = MAX_DOCUMENT_SELECTION_SIZE_BYTES / KILOBYTE_TO_BYTE;

export class DocumentPreparationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DocumentPreparationError';
  }
}

function isJpegFile(file: File): boolean {
  return file.type.toLowerCase() === JPEG_MIME_TYPE || /\.jpe?g$/i.test(file.name);
}

export function getDocumentSelectionError(file: File): string | undefined {
  const maximumSize = isJpegFile(file) ? MAX_DOCUMENT_SELECTION_SIZE_BYTES : MAX_FILE_SIZE_BYTES;
  if (file.size > maximumSize) {
    return `${file.name} is too large. Max supported file size is ${maximumSize / MEGA_BYTE_TO_BYTE} MB.`;
  }
}

async function resizeJpeg(file: File): Promise<File> {
  const bitmap = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  try {
    const scale = Math.min(1, MAX_IMAGE_DIMENSION_PX / Math.max(bitmap.width, bitmap.height));
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext('2d');
    if (!context) {
      throw new DocumentPreparationError('Unable to resize the photo.');
    }
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    const resized = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, JPEG_MIME_TYPE, JPEG_QUALITY));
    if (!resized || resized.size === 0 || resized.size > MAX_FILE_SIZE_BYTES) {
      throw new DocumentPreparationError('Unable to resize the photo to the upload limit.');
    }
    return new File([resized], file.name, { type: JPEG_MIME_TYPE, lastModified: file.lastModified });
  } finally {
    bitmap.close();
    canvas.width = 0;
    canvas.height = 0;
  }
}

export async function prepareDocumentForUpload(file: File): Promise<File> {
  const selectionError = getDocumentSelectionError(file);
  if (selectionError) {
    throw new DocumentPreparationError(selectionError);
  }
  const candidate = (await isHeicFile(file)) ? await convertHeicToJpeg(file) : file;
  // Both native picker conversion and our HEIC decoder can inflate the JPEG above the
  // compression endpoint's input cap. Resize before making any upload/compression request.
  return candidate.size > MAX_FILE_SIZE_BYTES ? resizeJpeg(candidate) : candidate;
}
